import React, {
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Button, Drawer } from 'antd';
import { Site } from '../../dto';
import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { useConnection } from '../../connection/connection';
import { useBusinessContext } from '../../context/BusinessContext';
import { useEvents } from '../../common/EventContext';

export type SiteCreateModalInstance = {
  create: () => void;
  edit: (site: Site) => void;
};

type Props = {};

const SiteCreateModal: ForwardRefRenderFunction<
  SiteCreateModalInstance,
  Props
> = (props, ref) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [site, setSite] = useState<Site>();
  const connection = useConnection();
  const { businessId } = useBusinessContext();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState<boolean>(false);
  const events = useEvents();
  const [mode, setMode] = useState<'create' | 'edit'>('create');

  useImperativeHandle(
    ref,
    () => ({
      create: () => {
        setVisible(true);
        form.resetFields();
        setMode('create');
      },
      edit: (site: Site) => {
        setVisible(true);
        form.resetFields();
        form.setFieldsValue(site);
        setSite(site);
        setMode('edit');
      },
    }),
    [form],
  );

  const schema: FormSchema = [
    {
      id: 'name',
      name: 'Site Name',
      type: 'string',
      props: {
        placeholder: 'Enter site name',
      },
      options: {
        rules: [{ required: true, message: 'Site Name is required' }],
      },
    },
    {
      id: 'status',
      name: 'Status',
      type: 'select',
      props: {
        options: [
          { label: 'Active', value: 'Active' },
          { label: 'Inactive', value: 'Inactive' },
        ],
      },
      options: {
        rules: [{ required: true, message: 'Status is required' }],
      },
    },
    {
      id: 'description',
      name: 'Description',
      type: 'text',
    },
  ];

  const submit = useCallback(
    (value: Omit<Site, 'siteId' | 'businessId'>) => {
      setSaving(true);
      if (mode === 'create') {
        connection
          .post(`sites/${businessId}`, value)
          .then((res) => {
            events.emit('SITE', { action: 'CREATE', data: res.data });
            setVisible(false);
          })
          .finally(() => setSaving(false));
      } else {
        connection
          .put(`sites/${businessId}/${site?.siteId}`, value)
          .then((res) => {
            events.emit('SITE', { action: 'UPDATE', data: res.data });
            setVisible(false);
          })
          .finally(() => setSaving(false));
      }
    },
    [businessId, connection, events, mode, site],
  );

  return (
    <Drawer
      title={mode === 'create' ? 'Create a Site' : 'Edit Site'}
      width={600}
      open={visible}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={() => setVisible(false)}>Cancel</Button>
          <Button loading={saving} type="primary" onClick={() => form.submit()}>
            Save
          </Button>
        </div>
      }
      onClose={() => setVisible(false)}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Items schema={schema} />
      </Form>
    </Drawer>
  );
};

export const useSiteCreateModal = () => useRef<SiteCreateModalInstance>(null);

export default React.forwardRef<SiteCreateModalInstance, Props>(
  SiteCreateModal,
);
